/**
 * PCISPH (Predictive-Corrective Incompressible SPH) Solver WGSL Shaders
 *
 * PCISPH iteratively enforces incompressibility through prediction-correction cycles:
 * 1. Predict particle positions
 * 2. Compute predicted density
 * 3. Update pressure to correct density error
 * 4. Apply pressure forces
 * 5. Repeat 2-4 for pcisphIter iterations (typically 3-5)
 *
 * Algorithm from "Predictive-Corrective Incompressible SPH" (Solenthaler & Pajarola, SIGGRAPH 2009)
 */

import {
  SPH_STRUCT_TANIMLARI,
  SPH_KERNEL_FONKSIYONLARI,
  SPH_GRID_YARDIMCI,
  SPH_ORTAK_BINDING_TANIMLARI,
  buildShader
} from './sph-ortak-shaders.wgsl';
import { GRID_NEIGHBOR_HELPER } from './sph-grid-shaders.wgsl';

/**
 * PCISPH Solver-Specific Bindings (@group(3))
 *
 * Buffers:
 * - pressure: Current pressure values (accumulated across iterations)
 * - pressureForce: Pressure force accumulator
 * - predictedPos: Predicted particle positions for current iteration
 * - predictedDens: Predicted density at predicted positions
 */
export const PCISPH_SOLVER_BINDINGS = `
@group(3) @binding(0) var<storage, read_write> pressure: array<f32>;
@group(3) @binding(1) var<storage, read_write> pressureForce: array<vec4<f32>>;
@group(3) @binding(2) var<storage, read_write> predictedPos: array<vec4<f32>>;
@group(3) @binding(3) var<storage, read_write> predictedDens: array<f32>;
`;

/**
 * Pass 1: Compute Density
 *
 * Standard SPH density computation: rho_i = sum(m_j * W(r_ij))
 * Also resets pressure values for new time step
 */
export const PCISPH_COMPUTE_DENSITY = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PCISPH_SOLVER_BINDINGS}
${GRID_NEIGHBOR_HELPER}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let pos_i = positionMass[i].xyz;
  let mass_i = 1.0 / positionMass[i].w;
  let h = params.smoothingRadius;
  let poly6C = params.poly6Const;

  var rho: f32 = 0.0;

  // Grid-based neighbor search
  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let r = pos_i - pos_j;
          let r2 = dot(r, r);

          if (r2 < h * h) {
            let mass_j = 1.0 / positionMass[j].w;
            rho += mass_j * kernelPoly6(r2, h, poly6C);
          }
        }
      }
    }
  }

  // Store density and reset pressure for new time step
  densityMisc[i] = vec4<f32>(rho, densityMisc[i].y, densityMisc[i].z, densityMisc[i].w);
  pressure[i] = 0.0;
}
`);

/**
 * Pass 2: Non-Pressure Forces
 *
 * Compute viscosity and surface tension forces (no pressure yet)
 * Gravity is applied during prediction phase
 */
export const PCISPH_NON_PRESSURE_FORCES = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PCISPH_SOLVER_BINDINGS}
${GRID_NEIGHBOR_HELPER}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let pos_i = positionMass[i].xyz;
  let vel_i = velocityAge[i].xyz;
  let mass_i = 1.0 / positionMass[i].w;
  let rho_i = max(densityMisc[i].x, 1.0);
  let h = params.smoothingRadius;
  let viscC = params.viscLapConst;
  let cohC = params.cohesionConst;

  var F = vec3<f32>(0.0);

  // Grid-based neighbor search
  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = positionMass[j].xyz;
          let r = pos_i - pos_j;
          let dist = length(r);

          if (dist < h && dist > 0.0001) {
            let mass_j = 1.0 / positionMass[j].w;
            let rho_j = max(densityMisc[j].x, 1.0);
            let vel_j = velocityAge[j].xyz;
            let v_ij = vel_j - vel_i;

            // Viscosity force
            let viscForce = mass_j / rho_j * params.viscosity * v_ij * kernelViscLap(dist, h, viscC);
            F += viscForce;

            // Surface tension (cohesion)
            let cohForce = -params.surfaceTension * mass_i * mass_j * kernelCohesion(dist, h, cohC) * (r / dist);
            F += cohForce;
          }
        }
      }
    }
  }

  // Store non-pressure forces
  forceVmax[i] = vec4<f32>(F, 0.0);
}
`);

/**
 * Pass 3: Predict Velocity and Position
 *
 * Predict intermediate velocity and position without pressure correction:
 * v* = v + dt * (F_nonpressure/rho + gravity)
 * x* = x + dt * v*
 */
export const PCISPH_PREDICT_VEL_POS = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PCISPH_SOLVER_BINDINGS}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let vel = velocityAge[i].xyz;
  let pos = positionMass[i].xyz;
  let F_nonp = forceVmax[i].xyz;
  let rho = max(densityMisc[i].x, 1.0);
  let dt = params.dt;

  let gravity = vec3<f32>(params.gravity_x, params.gravity_y, params.gravity_z);

  // Predict velocity with non-pressure forces + gravity
  let vStar = vel + dt * (F_nonp / rho + gravity);

  // Predict position
  let xStar = pos + dt * vStar;

  // Store predicted values
  predictedPos[i] = vec4<f32>(xStar, positionMass[i].w);

  // Temporarily store predicted velocity in pressureForce (will be overwritten)
  pressureForce[i] = vec4<f32>(vStar, 0.0);
}
`);

/**
 * Pass 4: Pressure Iteration (CRITICAL - Run 3-5 times)
 *
 * Iterative pressure solver:
 * 1. Compute predicted density at predicted positions: rho* = sum(m_j * W(x*_i - x*_j))
 * 2. Update pressure: p_i += delta * max(rho* - rho_0, 0)
 * 3. Compute pressure force: F_p = -m_i * sum(m_j * (p_i/rho_i^2 + p_j/rho_j^2) * grad_W)
 * 4. Update predicted velocity: v* += dt * F_p/rho_i
 * 5. Update predicted position: x* = x + dt * v*
 *
 * Delta scaling (simplified): delta ≈ 1 / (beta * constraint_gradient^2)
 * where beta = dt^2 * m^2 * 2 / rho_0^2
 */
export const PCISPH_PRESSURE_ITERATION = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_KERNEL_FONKSIYONLARI}
${SPH_GRID_YARDIMCI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PCISPH_SOLVER_BINDINGS}
${GRID_NEIGHBOR_HELPER}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let pos_i = predictedPos[i].xyz;
  let mass_i = 1.0 / positionMass[i].w;
  let h = params.smoothingRadius;
  let rho0 = params.restDensity;
  let rho_i = max(densityMisc[i].x, 1.0);
  let dt = params.dt;
  let poly6C = params.poly6Const;
  let spikyC = params.spikyGradConst;

  // Step 1: Compute predicted density at predicted position
  var rhoStar: f32 = 0.0;

  let gridOrigin = vec3<f32>(params.gridOrigin_x, params.gridOrigin_y, params.gridOrigin_z);
  let gridDim = vec3<u32>(params.gridDim_x, params.gridDim_y, params.gridDim_z);
  let myCell = posToCell(pos_i, gridOrigin, params.gridCellSize);

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = predictedPos[j].xyz;
          let r = pos_i - pos_j;
          let r2 = dot(r, r);

          if (r2 < h * h) {
            let mass_j = 1.0 / positionMass[j].w;
            rhoStar += mass_j * kernelPoly6(r2, h, poly6C);
          }
        }
      }
    }
  }

  predictedDens[i] = rhoStar;

  // Step 2: Update pressure (accumulate)
  // Delta scaling: simplified approximation
  // beta = dt^2 * m^2 * 2 / rho0^2
  let beta = dt * dt * mass_i * mass_i * 2.0 / (rho0 * rho0);
  let delta = 1.0 / max(beta * 1000.0, 1e-6); // Approximate scaling factor

  let densError = max(rhoStar - rho0, 0.0);
  pressure[i] += delta * densError;

  // Step 3: Compute pressure force
  var F_p = vec3<f32>(0.0);
  let p_i = pressure[i];

  for (var dz: i32 = -1; dz <= 1; dz++) {
    for (var dy: i32 = -1; dy <= 1; dy++) {
      for (var dx: i32 = -1; dx <= 1; dx++) {
        let nCell = myCell + vec3<i32>(dx, dy, dz);
        if (!cellInBounds(nCell, gridDim)) { continue; }

        let cellIdx = cellToIndex(nCell, gridDim);
        let start = prefixSum[cellIdx];
        let end = select(prefixSum[cellIdx + 1u], params.activeCount, cellIdx + 1u >= params.totalGridCells);

        for (var k = start; k < end; k++) {
          let j = sortedIndex[k];
          if (j == i) { continue; }

          let pos_j = predictedPos[j].xyz;
          let r = pos_i - pos_j;
          let dist = length(r);

          if (dist < h && dist > 0.0001) {
            let mass_j = 1.0 / positionMass[j].w;
            let p_j = pressure[j];
            let rho_j = max(densityMisc[j].x, 1.0);

            // Symmetric pressure gradient
            let grad = kernelSpikyGrad(r, dist, h, spikyC);
            F_p -= mass_j * (p_i / (rho_i * rho_i) + p_j / (rho_j * rho_j)) * grad;
          }
        }
      }
    }
  }

  F_p *= mass_i;
  pressureForce[i] = vec4<f32>(F_p, 0.0);

  // Step 4: Update predicted velocity with pressure correction
  let gravity = vec3<f32>(params.gravity_x, params.gravity_y, params.gravity_z);
  let F_nonp = forceVmax[i].xyz;
  let vel_base = velocityAge[i].xyz;

  let vel_i = vel_base + dt * (F_nonp / rho_i + gravity) + dt * F_p / rho_i;

  // Step 5: Update predicted position
  let xNew = positionMass[i].xyz + dt * vel_i;
  predictedPos[i] = vec4<f32>(xNew, positionMass[i].w);
}
`);

/**
 * Pass 5: Final Integration
 *
 * Apply final pressure-corrected velocity and position:
 * v_new = v + dt * (F_nonpressure/rho + gravity + F_pressure/rho)
 * x_new = x + dt * v_new
 */
export const PCISPH_FINAL_INTEGRATE = buildShader(`
${SPH_STRUCT_TANIMLARI}
${SPH_ORTAK_BINDING_TANIMLARI}
${PCISPH_SOLVER_BINDINGS}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= params.activeCount) { return; }

  let vel = velocityAge[i].xyz;
  let pos = positionMass[i].xyz;
  let F_nonp = forceVmax[i].xyz;
  let F_p = pressureForce[i].xyz;
  let rho_i = max(densityMisc[i].x, 1.0);
  let dt = params.dt;

  let gravity = vec3<f32>(params.gravity_x, params.gravity_y, params.gravity_z);

  // Final velocity with all forces
  let newVel = vel + dt * (F_nonp / rho_i + gravity + F_p / rho_i);

  // Final position
  let finalPos = pos + dt * newVel;

  // Update buffers
  positionMass[i] = vec4<f32>(finalPos, positionMass[i].w);
  velocityAge[i] = vec4<f32>(newVel, velocityAge[i].w + dt);

  // Clear force accumulator, store velocity magnitude
  forceVmax[i] = vec4<f32>(0.0, 0.0, 0.0, length(newVel));
}
`);

/**
 * Shader metadata for pipeline creation
 */
export const PCISPH_SHADER_METADATA = {
  computeDensity: {
    shader: PCISPH_COMPUTE_DENSITY,
    entryPoint: 'main',
    usesGrid: true,
    description: 'PCISPH: Compute particle density and reset pressure'
  },
  nonPressureForces: {
    shader: PCISPH_NON_PRESSURE_FORCES,
    entryPoint: 'main',
    usesGrid: true,
    description: 'PCISPH: Compute viscosity and surface tension forces'
  },
  predictVelPos: {
    shader: PCISPH_PREDICT_VEL_POS,
    entryPoint: 'main',
    usesGrid: false,
    description: 'PCISPH: Predict intermediate velocity and position'
  },
  pressureIteration: {
    shader: PCISPH_PRESSURE_ITERATION,
    entryPoint: 'main',
    usesGrid: true,
    description: 'PCISPH: Iterative pressure solver (run 3-5x)'
  },
  finalIntegrate: {
    shader: PCISPH_FINAL_INTEGRATE,
    entryPoint: 'main',
    usesGrid: false,
    description: 'PCISPH: Final velocity and position integration'
  }
};
